import { useForm, validators } from "@dbn/omnichannel-design-system-g4p";
import { boolean, option, taskEither } from "fp-ts";
import { constNull, pipe } from "fp-ts/function";
import { Option } from "fp-ts/Option";
import { useEffect, useRef, useState } from "react";
import { foldTenant } from "../../../../globalDomain";
import { useAppContext } from "../../../../useAppContext";
import { useValidators } from "../../../../Common/useValidators";
import {
  isReworked,
  useReworkComparator,
} from "../../../Rework/useReworkComparator";
import {
  IncomeCardProps,
  useControlledSubmit,
} from "../../commons/useControlledSubmit";
import {
  constOptionNoneValidator,
  getEmptyAllowanceInfo,
  getEmptyIncomeInfo,
} from "../../commons/Utils";
import { IncomeData, isOptionTrue } from "../../domain";
import { IncomeSectionCZ } from "./IncomeSection/IncomeSection";

const isOptionFalse = (value: Option<boolean>) => {
  return pipe(
    value,
    option.exists(value => value === false)
  );
};

const MIN_AMOUNT = -999999999;
const MAX_AMOUNT = 999999999;

export function RentCard(props: IncomeCardProps) {
  const { incomeData, options } = props;
  const {
    apiParameters: { tenant },
  } = useAppContext();

  const {
    definedNoExtract,
    amountMinMaxWithNegative,
    nonNegativeNumber,
  } = useValidators();

  const amountValidator = () =>
    validators.inSequence(
      definedNoExtract<number>(),
      validators.validateIfDefined(
        amountMinMaxWithNegative(MIN_AMOUNT, MAX_AMOUNT)
      )
    );

  const SKOnlyAmountValidator = foldTenant(
    tenant,
    amountValidator,
    constOptionNoneValidator
  );
  const validatedValues = useRef<Option<IncomeData>>(option.none);

  const { specialFieldsReworkFieldProps } = useReworkComparator(props.rework);

  const initialValues = {
    salaryCurrency: props.salaryCurrency,
    monthlyIncome: props.isMainIncome ? props.monthlyIncome : option.none,
    r39PartialTaxBase: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.r39PartialTaxBase)
    ),
    r201IncomeFromLease: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.r201IncomeFromLease)
    ),
    line75TaxReturn: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.line75TaxReturn)
    ),

    reInPersonalOwnership: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.reInPersonalOwnership)
    ),
    reCoOwned: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.reCoOwned)
    ),
    reUsedAsCollateral: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.reUsedAsCollateral)
    ),
    incomeFromRentContract: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.incomeFromRentContract)
    ),
    isApplicantDeclareAsUserInDeclaration: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.isApplicantDeclareAsUserInDeclaration)
    ),
    isCooperativeOwnership: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.isCooperativeOwnership)
    ),
    tax: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.tax)
    ),
    taxBase: pipe(
      incomeData.incomeInfo,
      option.chain(value => value.taxBase)
    ),
    hasAdditionalIncome: pipe(
      incomeData.incomeInfo,
      option.chain(data => data.hasAdditionalIncome)
    ),
    hasInfoFromTaxReturn: pipe(
      incomeData.incomeInfo,
      option.chain(data => data.hasInfoFromTaxReturn)
    ),
  };
  const [addInfoFromTaxReturn, setAddInfoFromTaxReturn] = useState(
    option.isSome(initialValues.hasInfoFromTaxReturn)
      ? initialValues.hasInfoFromTaxReturn.value
      : false
  );

  const { fieldProps, handleSubmit, resetForm } = useForm(
    {
      initialValues: initialValues,
      fieldValidators: values => ({
        salaryCurrency: () => definedNoExtract<string>()(props.salaryCurrency),
        monthlyIncome: () =>
          validators.inSequence(
            definedNoExtract<number>(),
            validators.validateIfDefined(nonNegativeNumber)
          )(props.monthlyIncome),
        r39PartialTaxBase: foldTenant(tenant, constOptionNoneValidator, () =>
          pipe(
            addInfoFromTaxReturn,
            boolean.fold(constOptionNoneValidator, amountValidator)
          )
        ),
        reInPersonalOwnership: foldTenant(
          tenant,
          constOptionNoneValidator,
          () => definedNoExtract<boolean>()
        ),
        reCoOwned: foldTenant(tenant, constOptionNoneValidator, () =>
          pipe(
            isOptionTrue(values.reInPersonalOwnership),
            boolean.fold(constOptionNoneValidator, () =>
              definedNoExtract<boolean>()
            )
          )
        ),
        r201IncomeFromLease: foldTenant(tenant, constOptionNoneValidator, () =>
          pipe(
            addInfoFromTaxReturn,
            boolean.fold(constOptionNoneValidator, amountValidator)
          )
        ),
        line75TaxReturn: foldTenant(tenant, constOptionNoneValidator, () =>
          pipe(
            addInfoFromTaxReturn,
            boolean.fold(constOptionNoneValidator, amountValidator)
          )
        ),
        reUsedAsCollateral: foldTenant(tenant, constOptionNoneValidator, () =>
          pipe(
            isOptionTrue(values.reInPersonalOwnership),
            boolean.fold(constOptionNoneValidator, () =>
              definedNoExtract<boolean>()
            )
          )
        ),
        incomeFromRentContract: foldTenant(
          tenant,
          constOptionNoneValidator,
          () =>
            pipe(
              addInfoFromTaxReturn,
              boolean.fold(constOptionNoneValidator, amountValidator)
            )
        ),
        isCooperativeOwnership: foldTenant(
          tenant,
          constOptionNoneValidator,
          () =>
            pipe(
              isOptionFalse(values.reInPersonalOwnership),
              boolean.fold(constOptionNoneValidator, () =>
                definedNoExtract<boolean>()
              )
            )
        ),
        isApplicantDeclareAsUserInDeclaration: foldTenant(
          tenant,
          constOptionNoneValidator,
          () =>
            pipe(
              isOptionTrue(values.isCooperativeOwnership),
              boolean.fold(constOptionNoneValidator, () =>
                definedNoExtract<boolean>()
              )
            )
        ),
        tax: pipe(
          addInfoFromTaxReturn,
          boolean.fold(constOptionNoneValidator, () => SKOnlyAmountValidator)
        ),
        taxBase: pipe(
          addInfoFromTaxReturn,
          boolean.fold(constOptionNoneValidator, () => SKOnlyAmountValidator)
        ),
        hasAdditionalIncome: props.isMainIncome
          ? definedNoExtract<boolean>()
          : undefined,
      }),
    },
    {
      onSubmit: data => {
        return taskEither.fromIO(
          () =>
            (validatedValues.current = option.some({
              ...incomeData,
              incomeInfo: option.some({
                ...getEmptyIncomeInfo("IncomeFromRent"),
                r39PartialTaxBase: data.r39PartialTaxBase,
                r201IncomeFromLease: data.r201IncomeFromLease,
                line75TaxReturn: data.line75TaxReturn,
                reInPersonalOwnership: data.reInPersonalOwnership,
                isApplicantDeclareAsUserInDeclaration:
                  data.isApplicantDeclareAsUserInDeclaration,
                isCooperativeOwnership: data.isCooperativeOwnership,
                reCoOwned: data.reCoOwned,
                reUsedAsCollateral: data.reUsedAsCollateral,
                incomeFromRentContract: data.incomeFromRentContract,
                tax: data.tax,
                taxBase: data.taxBase,
                salaryCurrency: data.salaryCurrency,
                monthlyIncome: data.monthlyIncome,
                hasAdditionalIncome: data.hasAdditionalIncome,
                hasInfoFromTaxReturn: option.some(addInfoFromTaxReturn),
              }),
              allowanceInfo: option.some({ ...getEmptyAllowanceInfo() }),
            }))
        );
      },
    }
  );
  useEffect(() => {
    resetForm();
  }, [props.employmentType]);
  useControlledSubmit(props, handleSubmit, validatedValues);

  const employmentTypeReworked = isReworked(
    props.rework,
    "contractInfo",
    "employmentType"
  );
  const incomeSourceReworked = isReworked(
    props.rework,
    "incomeInfo",
    "incomeSource"
  );
  const disabled = !options.isEditing;
  const reworkDependent = employmentTypeReworked || incomeSourceReworked;

  const keepAccountancyReworked = isReworked(
    props.rework,
    "incomeInfo",
    "keepsAccountancy"
  );
  const reInPersonalOwnershipReworked = isReworked(
    props.rework,
    "incomeInfo",
    "reInPersonalOwnership"
  );
  const isCooperativeOwnershipReworked = isReworked(
    props.rework,
    "incomeInfo",
    "isCooperativeOwnership"
  );

  const incomeSection = foldTenant(tenant, constNull, () => (
    <IncomeSectionCZ
      addInfoFromTaxReturn={addInfoFromTaxReturn}
      setAddInfoFromTaxReturn={setAddInfoFromTaxReturn}
      incomeFromRentContractFieldProps={specialFieldsReworkFieldProps(
        fieldProps("incomeFromRentContract"),
        reworkDependent || keepAccountancyReworked,
        isReworked(props.rework, "incomeInfo", "incomeFromRentContract") &&
          option.isSome(initialValues.incomeFromRentContract),
        disabled,
        props.reworkAll
      )}
      r201IncomeFromLeaseFieldProps={specialFieldsReworkFieldProps(
        fieldProps("r201IncomeFromLease"),
        reworkDependent || keepAccountancyReworked,
        isReworked(props.rework, "incomeInfo", "r201IncomeFromLease") &&
          option.isSome(initialValues.r201IncomeFromLease),
        disabled,
        props.reworkAll
      )}
      r39PartialTaxBaseFieldProps={specialFieldsReworkFieldProps(
        fieldProps("r39PartialTaxBase"),
        reworkDependent || keepAccountancyReworked,
        isReworked(props.rework, "incomeInfo", "r39PartialTaxBase") &&
          option.isSome(initialValues.r39PartialTaxBase),
        disabled,
        props.reworkAll
      )}
      line75ofTaxReturnFieldProps={specialFieldsReworkFieldProps(
        fieldProps("line75TaxReturn"),
        reworkDependent || keepAccountancyReworked,
        isReworked(props.rework, "incomeInfo", "line75TaxReturn") &&
          option.isSome(initialValues.line75TaxReturn),
        disabled,
        props.reworkAll
      )}
      reCoOwned={specialFieldsReworkFieldProps(
        fieldProps("reCoOwned"),
        reworkDependent || keepAccountancyReworked,
        isReworked(props.rework, "incomeInfo", "reCoOwned") &&
          option.isSome(initialValues.reCoOwned),
        disabled,
        props.reworkAll
      )}
      reInPersonalOwnershipFieldProps={specialFieldsReworkFieldProps(
        fieldProps("reInPersonalOwnership"),
        reworkDependent || keepAccountancyReworked,
        isReworked(props.rework, "incomeInfo", "reInPersonalOwnership") &&
          option.isSome(initialValues.reInPersonalOwnership),
        disabled,
        props.reworkAll
      )}
      reUsedAsCollateralFieldProps={specialFieldsReworkFieldProps(
        fieldProps("reUsedAsCollateral"),
        reworkDependent || keepAccountancyReworked,
        isReworked(props.rework, "incomeInfo", "reUsedAsCollateral") &&
          option.isSome(initialValues.reUsedAsCollateral),
        disabled,
        props.reworkAll
      )}
      isCooperativeOwnership={specialFieldsReworkFieldProps(
        fieldProps("isCooperativeOwnership"),
        reworkDependent ||
          keepAccountancyReworked ||
          reInPersonalOwnershipReworked,
        isReworked(props.rework, "incomeInfo", "isCooperativeOwnership") &&
          option.isSome(initialValues.isCooperativeOwnership),
        disabled,
        props.reworkAll
      )}
      isApplicantDeclareAsUserInDeclaration={specialFieldsReworkFieldProps(
        fieldProps("isApplicantDeclareAsUserInDeclaration"),
        reworkDependent ||
          keepAccountancyReworked ||
          reInPersonalOwnershipReworked ||
          isCooperativeOwnershipReworked,
        isReworked(
          props.rework,
          "incomeInfo",
          "isApplicantDeclareAsUserInDeclaration"
        ) && option.isSome(initialValues.isApplicantDeclareAsUserInDeclaration),
        disabled,
        props.reworkAll
      )}
      hasAdditionalIncome={specialFieldsReworkFieldProps(
        fieldProps("hasAdditionalIncome"),
        reworkDependent,
        isReworked(props.rework, "incomeInfo", "hasAdditionalIncome") &&
          option.isSome(initialValues.hasAdditionalIncome),
        disabled,
        props.reworkAll
      )}
      isMainIncome={props.isMainIncome}
      isEditing={props.options.isEditing}
    />
  ));

  return incomeSection;
}
